import { ethers, formatUnits, parseEther, parseUnits } from "ethers";
import "./index.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HashRouter,
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useParams,
  Link,
  NavLink,
} from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { tokensForBNB_Fourmeme_VCP } from "./helpers";

/* ===========================
   Config & helpers
   =========================== */
const BACKEND_BASE = "https://bsc-api.motionlabs.work"; //"http://localhost:3001";

const categories = [
  { value: "Meme", label: "Meme" },
  { value: "AI", label: "AI" },
  { value: "Defi", label: "Defi" },
  { value: "Games", label: "Games" },
  { value: "Infra", label: "Infra" },
  { value: "De-sci", label: "De-sci" },
  { value: "Social", label: "Social" },
  { value: "Depin", label: "Depin" },
  { value: "Charity", label: "Charity" },
  { value: "Others", label: "Others" },
];

function SelectInput({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col">
      <span className="mb-1 text-sm text-neutral-300">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm outline-none focus:border-neutral-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function getAccessKey(): string | null {
  return localStorage.getItem("accessKey");
}
function setAccessKey(k: string) {
  localStorage.setItem("accessKey", k);
}
function clearAccessKey() {
  localStorage.removeItem("accessKey");
}

async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const key = getAccessKey();
  const headers = new Headers(init.headers || {});
  if (key) headers.set("x-access-key", key);
  headers.set("Accept", "application/json");
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(input, { ...init, headers });
  return res;
}

function num(v: any, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function badgeClass(status: string) {
  return [
    "rounded px-3 py-1 text-sm border",
    status === "Launched" ||
    status === "Ready" ||
    status === "Launched successfully"
      ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/40"
      : status?.toLowerCase().includes("fund") ||
        status?.toLowerCase().includes("launch")
      ? "bg-blue-900/40 text-blue-300 border-blue-700/40"
      : status?.toLowerCase().includes("error") ||
        status?.toLowerCase().includes("failed")
      ? "bg-red-900/40 text-red-300 border-red-700/40"
      : "bg-neutral-800 text-neutral-300 border-neutral-700/50",
  ].join(" ");
}

function prettyBNBWei(v?: string | bigint | number) {
  if (v === null || v === undefined) return "0 BNB";
  let bi: bigint;
  try {
    bi = BigInt(v as any);
  } catch {
    return String(v);
  }
  const int = bi / 10n ** 18n;
  const frac = bi % 10n ** 18n;
  const fracStr = frac.toString().padStart(18, "0").slice(0, 6);
  return `${int.toString()}.${fracStr} BNB`;
}

/* ===========================
   App Shell
   =========================== */
function Shell({
  children,
  title,
  right,
}: {
  children: React.ReactNode;
  title: string;
  right?: React.ReactNode;
}) {
  const key = getAccessKey();
  const [idInput, setIdInput] = useState("");
  const [myLaunches, setMyLaunches] = useState<
    Array<{ id: string; name: string; symbol: string }>
  >([]);
  const [myErr, setMyErr] = useState<string>("");
  const [loadingMy, setLoadingMy] = useState(false);
  const [auto, setAuto] = useState(true);
  const navigate = useNavigate();

  const loadMy = useCallback(async () => {
    if (!key) return;
    try {
      setLoadingMy(true);
      setMyErr("");
      const res = await apiFetch(`${BACKEND_BASE}/launches/my`);
      const j = await res.json();
      if (!j?.success) {
        setMyErr(j?.message || "Failed to load launches");
        setMyLaunches([]);
        return;
      }
      setMyLaunches(j.data ?? []);
    } catch (e: any) {
      setMyErr(e?.message ?? "Failed to load launches");
    } finally {
      setLoadingMy(false);
    }
  }, [key]);

  useEffect(() => {
    loadMy();
  }, [loadMy]);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(loadMy, 5000);
    return () => clearInterval(t);
  }, [auto, loadMy]);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex">
      <Toaster position="top-center" reverseOrder={false} />
      <aside className="w-64 border-r border-neutral-800 bg-neutral-950/70">
        <div className="p-4 border-b border-neutral-800">
          <div className="text-lg font-semibold tracking-tight">
            Bundler Control
          </div>
          <div className="text-xs text-neutral-500">
            MotionLabs • Launch Manager
          </div>
        </div>

        <nav className="p-3 space-y-2">
          {!key ? (
            <NavLink
              to="/login"
              className={({ isActive }) =>
                `block w-full text-left px-3 py-2 rounded-md text-sm hover:bg-neutral-900 ${
                  isActive ? "bg-neutral-900" : ""
                }`
              }
            >
              🔐 Login
            </NavLink>
          ) : (
            <>
              <NavLink
                to="/create_launch"
                className={({ isActive }) =>
                  `block w-full text-left px-3 py-2 rounded-md text-sm hover:bg-neutral-900 ${
                    isActive ? "bg-neutral-900" : ""
                  }`
                }
              >
                ➕ Create Launch
              </NavLink>

              <div className="px-3 pt-3 text-xs uppercase tracking-wide text-neutral-500">
                Your Launches
              </div>
              <div className="px-2">
                <div className="flex items-center justify-between px-1 py-1">
                  <button
                    onClick={loadMy}
                    className="text-[11px] text-neutral-400 hover:text-neutral-200"
                  >
                    Refresh
                  </button>
                  <label className="text-[11px] text-neutral-500 flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={auto}
                      onChange={(e) => setAuto(e.target.checked)}
                    />
                    Auto
                  </label>
                </div>
                {loadingMy && myLaunches.length === 0 && (
                  <div className="px-1 py-2 text-[11px] text-neutral-500">
                    Loading…
                  </div>
                )}
                {myErr && (
                  <div className="px-1 py-2 text-[11px] text-red-400">
                    {myErr}
                  </div>
                )}
                {myLaunches.length === 0 && !loadingMy && !myErr && (
                  <div className="px-1 py-2 text-[11px] text-neutral-500">
                    No launches yet
                  </div>
                )}
                <div className="space-y-1">
                  {myLaunches.map((l) => (
                    <NavLink
                      key={l.id}
                      to={`/launches/${l.id}`}
                      className={({ isActive }) =>
                        `block w-full text-left px-3 py-2 rounded-md text-sm hover:bg-neutral-900 ${
                          isActive ? "bg-neutral-900" : ""
                        }`
                      }
                      title={`${l.name ?? l.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">
                          {l.name || l.id.slice(0, 10)}
                        </span>
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-neutral-800 text-neutral-300">
                          {l.symbol || "—"}
                        </span>
                      </div>
                    </NavLink>
                  ))}
                </div>

                <div className="mt-4 text-[11px] text-neutral-500">
                  Open by ID
                </div>
                <input
                  value={idInput}
                  onChange={(e) => setIdInput(e.target.value)}
                  placeholder="Paste launch ID"
                  className="mt-1 w-full rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-xs outline-none focus:border-neutral-500"
                />
                <button
                  onClick={() => idInput && navigate(`/launches/${idInput}`)}
                  className="mt-2 w-full rounded bg-neutral-800 px-2 py-1.5 text-xs hover:bg-neutral-700"
                >
                  Open
                </button>
              </div>

              <div className="px-3 pt-4 text-[11px] text-neutral-500">
                Access Key:
              </div>
              <div className="px-3">
                <code className="text-[10px] break-all text-neutral-400">
                  {key?.slice(0, 4)}…{key?.slice(-4)}
                </code>
                <div className="mt-2 flex gap-2">
                  <button
                    className="rounded border border-neutral-700 px-2 py-0.5 text-[11px] hover:bg-neutral-800"
                    onClick={() => {
                      navigator.clipboard.writeText(key!);
                    }}
                  >
                    Copy
                  </button>
                  <button
                    className="rounded border border-red-700 text-red-300 px-2 py-0.5 text-[11px] hover:bg-red-900/30"
                    onClick={() => {
                      clearAccessKey();
                      window.location.href = "/login";
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </>
          )}
        </nav>
      </aside>

      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <header className="mb-8 flex items-center justify-between">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            <div>{right}</div>
          </header>
          {children}
        </div>
      </main>
    </div>
  );
}

/* ===========================
   Login
   =========================== */
function LoginPage() {
  const [accessKey, setAccessKeyState] = useState(getAccessKey() || "");
  const [msg, setMsg] = useState<string>("");
  const navigate = useNavigate();

  const doLogin = () => {
    const k = accessKey.trim();
    if (!k) {
      setMsg("Please paste your Access Key.");
      return;
    }
    setAccessKey(k);
    navigate("/create_launch");
  };

  return (
    <Shell title="Login">
      <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5 max-w-xl">
        <h2 className="mb-4 text-lg font-medium">Enter Access Key</h2>
        <label className="flex flex-col gap-2">
          <span className="text-sm text-neutral-300">Access Key</span>
          <input
            value={accessKey}
            onChange={(e) => setAccessKeyState(e.target.value)}
            placeholder="Paste your access key"
            className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm outline-none focus:border-neutral-500"
          />
        </label>
        <div className="mt-4 flex gap-2">
          <button
            onClick={doLogin}
            className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            Save & Continue
          </button>
          <Link
            to="/create_launch"
            className="rounded-md bg-neutral-700 px-5 py-2 text-sm font-medium text-white hover:bg-neutral-600"
            onClick={() => {
              if (accessKey.trim()) setAccessKey(accessKey.trim());
            }}
          >
            Skip (uses stored key)
          </Link>
        </div>
        {msg && (
          <div className="mt-3 rounded-md border border-neutral-800 bg-neutral-950 p-3 text-xs text-neutral-400">
            {msg}
          </div>
        )}
      </section>
    </Shell>
  );
}

/* ===========================
   Create Launch (POST /launches/create)
   =========================== */
type CreateLaunchForm = {
  metadata: {
    name: string;
    symbol: string;
    description: string;
    image_url: string; // Data URI
    website_url: string;
    twitter_url: string;
    telegram_url: string;
    label: string;
  };
  buySettings: {
    wallet_buy: number; // BNB
    dev_buy: number; // BNB
    total_wallets: number; // 20–100
  };
  settings: {
    tip_amount: number; // BNB, 0.025–1
    custom_deployer_private_key?: string;
  };
};

function CreateLaunchPage() {
  const navigate = useNavigate();
  const key = getAccessKey();
  useEffect(() => {
    if (!key) navigate("/login");
  }, [key, navigate]);

  const [form, setForm] = useState<CreateLaunchForm>({
    metadata: {
      name: "",
      symbol: "",
      description: "",
      image_url: "", // will be Data URI
      website_url: "",
      twitter_url: "",
      telegram_url: "",
      label: "Meme",
    },
    buySettings: {
      wallet_buy: 5,
      dev_buy: 5,
      total_wallets: 40,
    },
    settings: {
      tip_amount: 0.05,
      custom_deployer_private_key: "",
    },
  });
  const [statusMsg, setStatusMsg] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const totalBuy = useMemo(
    () => num(form.buySettings.wallet_buy) + num(form.buySettings.dev_buy),
    [form.buySettings.wallet_buy, form.buySettings.dev_buy]
  );

  const totalBuyPercentage = useMemo(() => {
    console.log("totalBuy", totalBuy);
    const expectedTokens = tokensForBNB_Fourmeme_VCP(
      parseEther(totalBuy.toString()),
      0n
    );
    const percentage =
      (Number(formatUnits(expectedTokens.toString(), 18)) / 1_000_000_000) *
      100;
    return percentage.toFixed(2);
  }, [totalBuy]);

  const valid =
    form.metadata.name &&
    form.metadata.symbol &&
    form.metadata.description &&
    form.metadata.image_url && // must be data URI now
    form.metadata.label &&
    form.buySettings.wallet_buy > 0 &&
    form.buySettings.dev_buy >= 0 &&
    form.buySettings.total_wallets >= 20 &&
    form.buySettings.total_wallets <= 100 &&
    form.settings.tip_amount >= 0.02 &&
    form.settings.tip_amount <= 1 &&
    totalBuy <= 17.9;

  const onPickImage = async (file: File) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const dataURI = typeof reader.result === "string" ? reader.result : "";
      setForm((s) => ({
        ...s,
        metadata: { ...s.metadata, image_url: dataURI },
      }));
    };
    reader.readAsDataURL(file); // <<<<<<<<<< DATA URI
  };

  const create = async () => {
    if (!valid) {
      setStatusMsg("Please fill required fields and respect constraints.");
      return;
    }
    try {
      setStatusMsg("Creating…");
      const res = await apiFetch(`${BACKEND_BASE}/launches/create`, {
        method: "POST",
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data?.success) {
        setStatusMsg(data?.message || "Create failed");
        return;
      }
      const launch = data?.launch;
      const id = launch?.id || data?.id;
      setStatusMsg("Created.");
      if (id) navigate(`/launches/${id}`);
    } catch (e: any) {
      setStatusMsg(e?.message ?? "Create failed");
    }
  };

  return (
    <Shell
      title="Create Launch (BNB)"
      right={<span className={badgeClass("Idle")}>Status: Idle</span>}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Metadata */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5">
          <h2 className="mb-4 text-lg font-medium">Metadata</h2>
          <div className="space-y-3">
            <TextInput
              label="Name"
              value={form.metadata.name}
              onChange={(v) =>
                setForm((s) => ({ ...s, metadata: { ...s.metadata, name: v } }))
              }
            />
            <TextInput
              label="Symbol"
              value={form.metadata.symbol}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  metadata: { ...s.metadata, symbol: v },
                }))
              }
            />
            <TextArea
              label="Description"
              value={form.metadata.description}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  metadata: { ...s.metadata, description: v },
                }))
              }
            />
            <TextInput
              label="Website URL"
              value={form.metadata.website_url}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  metadata: { ...s.metadata, website_url: v },
                }))
              }
            />
            <TextInput
              label="Twitter URL"
              value={form.metadata.twitter_url}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  metadata: { ...s.metadata, twitter_url: v },
                }))
              }
            />
            <TextInput
              label="Telegram URL"
              value={form.metadata.telegram_url}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  metadata: { ...s.metadata, telegram_url: v },
                }))
              }
            />
            <SelectInput
              label="Label"
              value={form.metadata.label}
              options={categories}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  metadata: { ...s.metadata, label: v },
                }))
              }
            />

            {/* Image picker → Data URI */}
            <div className="md:col-span-2">
              <span className="mb-1 block text-sm text-neutral-300">
                Image (Data URI)
              </span>
              <div
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-700 bg-neutral-900/60 p-6 text-center hover:border-neutral-500"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const f = e.dataTransfer.files?.[0];
                  if (f) onPickImage(f);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png, image/jpeg, image/svg+xml"
                  max={3 * 1024 * 1024} // 3MB
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPickImage(f);
                  }}
                />
                {form.metadata.image_url ? (
                  <>
                    <img
                      src={form.metadata.image_url}
                      alt="token"
                      className="mb-3 h-24 w-24 rounded-md object-cover"
                    />
                    <div className="text-sm text-neutral-300">
                      {imageFile?.name ?? "Selected image"} •{" "}
                      {(imageFile?.size ?? 0) / 1024 < 1024
                        ? `${
                            Math.round(((imageFile?.size ?? 0) / 1024) * 10) /
                            10
                          } KB`
                        : `${
                            Math.round(
                              ((imageFile?.size ?? 0) / (1024 * 1024)) * 10
                            ) / 10
                          } MB`}
                    </div>
                    <div className="mt-2 text-xs text-neutral-500">
                      Click to replace
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-2 text-sm text-neutral-300">
                      Drop image here or click to upload
                    </div>
                    <div className="text-xs text-neutral-500">PNG/JPG/SVG</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Buy Settings & Tip */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5">
          <h2 className="mb-4 text-lg font-medium">Buy Settings & Tip</h2>
          <div className="grid grid-cols-3 gap-3">
            <NumberInput
              label="Wallet Buy (BNB)"
              min={0}
              max={17.9}
              step={0.01}
              value={form.buySettings.wallet_buy}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  buySettings: { ...s.buySettings, wallet_buy: v },
                }))
              }
            />
            <NumberInput
              label="Dev Buy (BNB)"
              min={0}
              max={17.9}
              step={0.01}
              value={form.buySettings.dev_buy}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  buySettings: { ...s.buySettings, dev_buy: v },
                }))
              }
            />
            <NumberInput
              label="Total Wallets (20–100)"
              min={20}
              max={100}
              step={1}
              value={form.buySettings.total_wallets}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  buySettings: { ...s.buySettings, total_wallets: v },
                }))
              }
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <NumberInput
              label="Tip Amount (BNB, 0.02–1)"
              min={0.02}
              max={1}
              step={0.001}
              value={form.settings.tip_amount}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  settings: { ...s.settings, tip_amount: v },
                }))
              }
            />
            <TextInput
              label="Custom Deployer Private Key (optional)"
              value={form.settings.custom_deployer_private_key || ""}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  settings: {
                    ...s.settings,
                    custom_deployer_private_key: v || undefined,
                  },
                }))
              }
            />
          </div>
          <div className="mt-3 text-xs text-neutral-400">
            Total buy (wallet+dev) must be ≤ 17.9 BNB. Tip must be 0.01–1 BNB.
            Wallets 20–100.
          </div>

          <div className="mt-3 text-m text-neutral-400">
            Expected supply: <code>{totalBuyPercentage}%</code>
          </div>

          <div className="mt-5">
            <button
              onClick={create}
              disabled={!valid}
              className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              Create Launch
            </button>
          </div>

          {statusMsg && (
            <div className="mt-3 rounded-md border border-neutral-800 bg-neutral-950 p-3 text-xs text-neutral-400">
              {statusMsg}
            </div>
          )}
        </section>

        {/* Preview */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5 md:col-span-2">
          <h2 className="mb-3 text-lg font-medium">Request Preview</h2>
          <pre className="text-xs overflow-x-auto rounded border border-neutral-800 bg-neutral-950 p-3">
            {JSON.stringify(form, null, 2)}
          </pre>
        </section>
      </div>
    </Shell>
  );
}

/* ===========================
   Launch Details (maps to your API)
   =========================== */
type LaunchGetResponse = {
  success: boolean;
  data?: any;
  message?: string;
};

function MonoCopy({ text, short = false }: { text: string; short?: boolean }) {
  const view =
    short && text?.length > 12 ? `${text.slice(0, 6)}…${text.slice(-6)}` : text;
  return (
    <span className="inline-flex items-center gap-2">
      <code className="font-mono text-xs text-neutral-100 break-all">
        {view}
      </code>
      <button
        className="rounded border border-neutral-700 px-2 py-0.5 text-[11px] text-neutral-300 hover:bg-neutral-800"
        onClick={() => navigator.clipboard.writeText(text)}
      >
        Copy
      </button>
    </span>
  );
}

function Rows({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <div className="space-y-2 text-sm text-neutral-300">
      {items.map(([l, v]) => (
        <div className="flex items-center justify-between" key={l}>
          <span>{l}</span>
          <span className="font-mono text-neutral-200">{v}</span>
        </div>
      ))}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-400">
      {children}
    </th>
  );
}
function Td({
  children,
  mono = false,
}: {
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <td
      className={`whitespace-nowrap px-3 py-2 ${
        mono ? "font-mono text-sm text-neutral-300" : ""
      }`}
    >
      {children}
    </td>
  );
}

function LaunchDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const key = getAccessKey();
  useEffect(() => {
    if (!key) navigate("/login");
  }, [key, navigate]);

  const [launch, setLaunch] = useState<any | null>(null);
  const [status, setStatus] = useState<string>("Idle");
  const [auto, setAuto] = useState<boolean>(true);
  const [err, setErr] = useState<string>("");

  const [sellSelection, setSellSelection] = useState<Record<string, boolean>>(
    {}
  );
  const [sellBps, setSellBps] = useState<number>(10);
  const [sellAll, setSellAll] = useState<boolean>(false);

  const fetchLaunch = useCallback(async () => {
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/get/${encodeURIComponent(id)}`
      );
      const data: LaunchGetResponse = await res.json();
      if (!data?.success) {
        setErr(data?.message || "Failed to fetch");
        return;
      }
      setLaunch(data.data);
      setStatus(data.data?.status || "Idle");
      setErr("");
      // initialize selection with all wallets false
      const wallets = [
        data.data?.buySettings?.[0]?.wallet_address,
        ...(data.data?.sellOrder ?? []),
      ].filter(Boolean) as string[];
      const initial: Record<string, boolean> = {};
      wallets.forEach((w) => (initial[w] = false));
      setSellSelection((cur) => ({ ...initial, ...cur })); // keep existing choices if any
    } catch (e: any) {
      setErr(e?.message ?? "Failed to fetch");
    }
  }, [id]);

  useEffect(() => {
    fetchLaunch();
  }, [fetchLaunch]);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(fetchLaunch, 3000);
    return () => clearInterval(t);
  }, [auto, fetchLaunch]);

  // ---- Actions ----
  const fund = async () => {
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/fund/${encodeURIComponent(id)}`
      );
      const j = await res.json();
      if (!j?.success) toast.error(j?.message || "Funding failed");
      else toast.success("OK. Funding...");
      fetchLaunch();
    } catch (e: any) {
      toast.error(e?.message ?? "Funding failed");
    }
  };

  const [isLaunching, setIsLaunching] = useState(false);

  const launchNow = async () => {
    try {
      setIsLaunching(true);
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/launch/${encodeURIComponent(id)}`
      );
      const j = await res.json();
      if (!j?.success) toast.error(j?.message || "Launch failed");
      else toast.success("Launched!");
      fetchLaunch();
    } catch (e: any) {
      toast.error(e?.message ?? "Launch failed");
    } finally {
      setIsLaunching(false);
    }
  };

  const collectNative = async () => {
    if (!launch) return;
    const destination = prompt("Destination BNB address:");
    if (!destination) return;
    const bpsStr = prompt("Percentage to collect:", "100");
    if (!bpsStr) return;
    const raw = Math.max(0, Math.min(100, Number(bpsStr)));
    const bps = Math.max(0, Math.min(10000, Math.floor(raw * 100)));
    const source_wallets: string[] = [
      launch.fundWallet?.address,
      launch.buySettings?.[0]?.wallet_address,
      ...(launch.sellOrder ?? []),
    ].filter(Boolean);
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/collect/native/${encodeURIComponent(id)}`,
        {
          method: "POST",
          body: JSON.stringify({
            percentage: bps,
            destination_wallet: destination,
            source_wallets,
          }),
        }
      );
      const j = await res.json();
      if (!j?.success) toast.error(j?.message || "Collect native failed");
      else toast.success(`OK. Collecting.`);
      fetchLaunch();
    } catch (e: any) {
      toast.error(e?.message ?? "Collect native failed");
    }
  };

  const collectToken = async () => {
    if (!launch) return;
    if (!launch?.states?.token_address) {
      toast.error("Token address not found on this launch.");
      return;
    }
    const destination = prompt("Destination token receiver (BNB address):");
    if (!destination) return;
    const bpsStr = prompt("Percentage to collect:", "100");
    if (!bpsStr) return;
    const raw = Math.max(0, Math.min(100, Number(bpsStr)));
    const bps = Math.max(0, Math.min(10000, Math.floor(raw * 100)));
    const source_wallets: string[] = [
      launch.buySettings?.[0]?.wallet_address,
      ...(launch.sellOrder ?? []),
    ].filter(Boolean);
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/collect/token/${encodeURIComponent(id)}`,
        {
          method: "POST",
          body: JSON.stringify({
            percentage: bps,
            destination_wallet: destination,
            source_wallets,
          }),
        }
      );
      const j = await res.json();
      if (!j?.success) toast.error(j?.message || "Collect token failed");
      else toast.success("Token collecting.");
      fetchLaunch();
    } catch (e: any) {
      toast.error(e?.message ?? "Collect token failed");
    }
  };

  const exportDev = async () => {
    if (!launch) return;
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/${encodeURIComponent(id)}/dev_private_key`,
        {
          method: "GET",
        }
      );
      const j = await res.json();
      if (!j?.success)
        toast.error(j?.message || "Export dev private key failed");
      else {
        navigator.clipboard.writeText(j.data);
        toast.success("Dev private key copied to clipboard");
      }
    } catch (error) {
      toast.error(error?.message ?? "Export dev private key failed");
    }
  };

  const forceSellMev = async () => {
    if (!launch) return;
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/force_mev_sell/${encodeURIComponent(id)}`,
        {
          method: "GET",
        }
      );
      const j = await res.json();
      if (!j?.success) toast.error(j?.message || "Force sell MEV failed");
      else {
        toast.success("Force sell MEV queued");
      }
    } catch (error) {
      toast.error(error?.message ?? "Force sell MEV failed");
    }
  };

  // Standard sell
  const sell = async (opts: { dev: boolean; all?: boolean; bps?: number }) => {
    const bps = Math.max(0, Math.min(10000, Math.floor(opts.bps ?? sellBps)));
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/sell/${encodeURIComponent(id)}`,
        {
          method: "POST",
          body: JSON.stringify({
            percentage: bps,
            isDevSell: !!opts.dev,
            isSellAll: !!opts.all,
          }),
        }
      );
      const j = await res.json();
      if (!j?.success) toast.error(j?.message || "Sell failed");
      else {
        let checkPromiseResolve: (value: any) => void;
        let checkPromiseReject: (reason: any) => void;
        let checkPromise = new Promise((resolve, reject) => {
          checkPromiseResolve = resolve;
          checkPromiseReject = reject;
        });

        // Set timeout to reject after 30 seconds
        const timeoutId = setTimeout(() => {
          clearInterval(interval);
          checkPromiseReject(
            new Error("Sell operation timed out after 30 seconds")
          );
        }, j?.total_wallets * 40_000);

        let interval = setInterval(async () => {
          const resSellCheck = await apiFetch(
            `${BACKEND_BASE}/launches/sells/${encodeURIComponent(j?.id)}`,
            {
              method: "GET",
            }
          );
          const jSellCheck = await resSellCheck.json();
          if (!jSellCheck?.success) {
            clearTimeout(timeoutId);
            clearInterval(interval);
            checkPromiseReject(new Error(jSellCheck?.message || "Sell failed"));
          } else {
            if (jSellCheck?.data?.is_finished) {
              clearTimeout(timeoutId);
              clearInterval(interval);
              checkPromiseResolve(jSellCheck);
            }
          }
        }, 500);

        toast.promise(checkPromise, {
          loading: `Selling ${opts.all ? "all" : `${opts.bps! / 100}%`}${
            opts.dev ? " from dev" : ` from ${j?.total_wallets} wallets`
          }...`,
          success: "Sold successfully",
          error: "Selling failed",
        });
      }
      fetchLaunch();
    } catch (e: any) {
      toast.error(e?.message ?? "Sell failed");
    }
  };

  // Sell specific wallets (frontend passes target_wallets; backend can ignore safely if not supported)
  const sellSpecific = async () => {
    if (!launch) return;
    const selected = Object.entries(sellSelection)
      .filter(([, v]) => v)
      .map(([addr]) => addr);
    if (selected.length === 0) {
      toast.error("Select at least one wallet.");
      return;
    }
    const bps = Math.max(0, Math.min(10000, Math.floor(sellBps)));
    try {
      const res = await apiFetch(
        `${BACKEND_BASE}/launches/sell/${encodeURIComponent(id)}`,
        {
          method: "POST",
          body: JSON.stringify({
            percentage: bps,
            isDevSell: false,
            isSellAll: false,
            // NEW: optional hint for backend if implemented
            target_wallets: selected,
          }),
        }
      );
      const j = await res.json();
      if (!j?.success) {
        toast.error(j?.message || "Sell (specific) failed");
      } else {
        toast.success(
          `Sell (specific) queued for ${selected.length} wallet(s). Mode=${
            j.sellMode ?? "n/a"
          }`
        );
      }
      fetchLaunch();
    } catch (e: any) {
      toast.error(e?.message ?? "Sell (specific) failed");
    }
  };

  const right = (
    <span className={badgeClass(status)}>Status: {status || "Idle"}</span>
  );

  // derive wallet list for UI
  const walletList: string[] = useMemo(() => {
    const list = [
      launch?.buySettings?.[0]?.wallet_address,
      ...(launch?.sellOrder ?? []),
    ].filter(Boolean);
    return Array.from(new Set(list));
  }, [launch]);

  return (
    <Shell title={`Launch • ${id}`} right={right}>
      {!launch ? (
        <div className="text-neutral-400">{err || "Loading…"}</div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Overview */}
          <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5">
            <h2 className="mb-4 text-lg font-medium">Overview</h2>
            <Rows
              items={[
                ["Status", <code key="s">{launch.status}</code>],
                [
                  "Token Address",
                  launch?.states?.token_address ||
                  launch?.states?.expected_token_address ? (
                    <MonoCopy
                      key="t"
                      text={
                        launch.states.token_address ||
                        launch.states.expected_token_address
                      }
                      short
                    />
                  ) : (
                    <span key="nt">—</span>
                  ),
                ],
                ["Name", <code key="n">{launch.metadata?.name}</code>],
                ["Symbol", <code key="sy">{launch.metadata?.symbol}</code>],
                [
                  "Funding Needed",
                  <code key="fn">
                    {prettyBNBWei(launch?.fundingState?.totalFundingNeeded)}
                  </code>,
                ],
                [
                  "Funding Current",
                  <code key="fc">
                    {prettyBNBWei(launch?.fundingState?.currentFunding)}
                  </code>,
                ],
                [
                  "Funding Deficit",
                  <code key="fd">
                    {prettyBNBWei(launch?.fundingState?.deficit)}
                  </code>,
                ],
                [
                  "Funding Wallet",
                  launch?.fundWallet?.address ? (
                    <MonoCopy key="fw" text={launch.fundWallet.address} short />
                  ) : (
                    <span key="nf">—</span>
                  ),
                ],
                [
                  "BNB In Wallets",
                  <code key="fn">
                    {prettyBNBWei(
                      Object.values(
                        launch.balancesMap as Record<string, any>
                      ).reduce(
                        (acc, v) => BigInt(acc) + BigInt(v?.native ?? 0),
                        0
                      )
                    )}
                  </code>,
                ],
                [
                  "Invested",
                  <code key="fn">
                    {prettyBNBWei(launch.sells.total_invested)}{" "}
                  </code>,
                ],
                [
                  "Sold",
                  <code key="fn">
                    {prettyBNBWei(launch.sells.total_sold)}{" "}
                  </code>,
                ],
                [
                  "Realized PnL",
                  <code
                    key="fn"
                    className={
                      launch.realizedProfit === 0
                        ? ""
                        : launch.realizedProfit > 0
                        ? "text-green-500"
                        : "text-red-500"
                    }
                  >
                    {launch.realizedProfit.toFixed(4)} BNB{" "}
                  </code>,
                ],
                [
                  "Remaining Supply",
                  <code key="fn">
                    {(
                      (Number(
                        ethers.formatEther(
                          Object.values(
                            launch.balancesMap as Record<string, any>
                          ).reduce(
                            (acc, v) => BigInt(acc) + BigInt(v?.token ?? 0),
                            0
                          )
                        )
                      ) /
                        1000000000) *
                      100
                    ).toFixed(2)}{" "}
                    %{" "}
                  </code>,
                ],
                [
                  "Funding Ready",
                  <code key="r">
                    {String(launch?.states?.is_funding_wallet_ready)}
                  </code>,
                ],
                [
                  "Is Funded",
                  <code key="f">{String(launch?.states?.is_funded)}</code>,
                ],
                [
                  "Is Launched",
                  <code key="l">{String(launch?.states?.is_launched)}</code>,
                ],
              ]}
            />

            <div className="mt-4 flex items-center gap-3 text-xs text-neutral-400">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={auto}
                  onChange={(e) => setAuto(e.target.checked)}
                />
                Auto refresh
              </label>
              <button
                onClick={fetchLaunch}
                className="rounded border border-neutral-700 px-2 py-1 hover:bg-neutral-800"
              >
                Refresh now
              </button>
            </div>
          </section>

          {/* Actions */}
          <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5">
            <h2 className="mb-4 text-lg font-medium">Actions</h2>

            {/* Standard sell controls */}
            <div className="mb-3 grid grid-cols-3 gap-3">
              <NumberInput
                label="Sell %"
                min={0}
                max={100}
                step={0.01}
                value={sellBps / 100}
                onChange={(v) => setSellBps(v * 100)}
              />

              <label className="flex flex-col">
                <span className="mb-1 text-sm text-neutral-300">Sell All?</span>
                <div className="h-[38px] flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sellAll}
                    onChange={(e) => setSellAll(e.target.checked)}
                  />
                  <span className="text-sm text-neutral-400">
                    Ignore %; sell full
                  </span>
                </div>
              </label>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => sell({ dev: false, all: sellAll, bps: sellBps })}
                className="rounded-md bg-neutral-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500 disabled:opacity-50"
                disabled={!launch?.states?.is_launched || isLaunching}
                title={!launch?.states?.is_launched ? "Not launched" : ""}
              >
                Sell
              </button>

              <button
                onClick={collectNative}
                className="rounded-md bg-neutral-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500"
              >
                Collect BNB
              </button>
              <button
                onClick={collectToken}
                className="rounded-md bg-neutral-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500"
              >
                Airdrop
              </button>

              <button
                onClick={fund}
                className="rounded-md bg-neutral-700 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-600 disabled:opacity-50"
                disabled={
                  !launch?.states?.is_funding_wallet_ready ||
                  launch?.states?.is_funded
                }
                title={
                  !launch?.states?.is_funding_wallet_ready
                    ? "Funding wallet not ready"
                    : ""
                }
              >
                Fund
              </button>
              <button
                onClick={launchNow}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
                disabled={
                  !launch?.states?.is_funded || launch?.states?.is_launched
                }
                title={!launch?.states?.is_funded ? "Not funded" : ""}
              >
                Launch
              </button>
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              <button
                onClick={() => sell({ dev: true, all: false, bps: 5000 })}
                className="rounded-md bg-neutral-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500 disabled:opacity-50"
                disabled={!launch?.states?.is_launched}
                title={!launch?.states?.is_launched ? "Not launched" : ""}
              >
                Sell 50% Dev
              </button>
              <button
                onClick={() => sell({ dev: true, all: true, bps: 10000 })}
                className="rounded-md bg-neutral-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500 disabled:opacity-50"
                disabled={!launch?.states?.is_launched}
                title={!launch?.states?.is_launched ? "Not launched" : ""}
              >
                Sell 100% Dev
              </button>
              <button
                onClick={() => exportDev()}
                className="rounded-md bg-neutral-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500 disabled:opacity-50"
                disabled={!launch?.states?.is_launched}
                title={!launch?.states?.is_launched ? "Not launched" : ""}
              >
                Copy Dev Private Key
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mt-12">
              <button
                onClick={() => forceSellMev()}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-500 disabled:opacity-50"
                disabled={
                  !launch?.states?.is_waiting_for_mev ||
                  launch?.states?.is_launched
                }
                title={
                  !launch?.states?.is_waiting_for_mev
                    ? "Not waiting for MEV"
                    : launch?.states?.is_launched
                    ? "Already launched"
                    : ""
                }
              >
                Manually sell MEV
              </button>
            </div>
          </section>

          {/* Balances */}
          <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5 md:col-span-2">
            <h2 className="mb-4 text-lg font-medium">Balances</h2>
            {!launch?.balancesMap ? (
              <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-sm text-neutral-400">
                No balances found in response.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-800">
                  <thead className="bg-neutral-950/60">
                    <tr>
                      <Th>Address</Th>
                      <Th>Native</Th>
                      <Th>Token</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800 bg-neutral-950/30">
                    {Object.entries<any>(launch.balancesMap).map(
                      ([addr, v], i) => (
                        <tr key={addr} className="hover:bg-neutral-900/50">
                          <Td>
                            <MonoCopy text={addr} short />{" "}
                            {i === 0 ? "(DEV)" : ""}
                          </Td>
                          <Td>{prettyBNBWei(v?.native ?? 0)}</Td>
                          <Td>
                            {(
                              (parseFloat(ethers.formatEther(v?.token ?? 0)) /
                                1000000000) *
                              100
                            ).toFixed(2)}
                            %
                          </Td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Buy settings (public view) */}
          <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-5 md:col-span-2">
            <h2 className="mb-4 text-lg font-medium">Buy Settings (Public)</h2>
            {!Array.isArray(launch?.buySettings) ||
            !launch?.buySettings?.length ? (
              <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-sm text-neutral-400">
                Not available.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-800">
                  <thead className="bg-neutral-950/60">
                    <tr>
                      <Th>#</Th>
                      <Th>Wallet</Th>
                      <Th>Native (planned)</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800 bg-neutral-950/30">
                    {launch.buySettings.map((w: any, i: number) => (
                      <tr
                        key={w.wallet_address}
                        className="hover:bg-neutral-900/50"
                      >
                        <Td mono>{i}</Td>
                        <Td>
                          <MonoCopy text={w.wallet_address} short />
                        </Td>
                        <Td>{prettyBNBWei(w.native)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </Shell>
  );
}

/* ===========================
   Input components
   =========================== */
function TextInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col">
      <span className="mb-1 text-sm text-neutral-300">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm outline-none focus:border-neutral-500"
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col">
      <span className="mb-1 text-sm text-neutral-300">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-h-[80px] rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm outline-none focus:border-neutral-500"
      />
    </label>
  );
}

function NumberInput({
  label,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  min?: number;
  max?: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col">
      <span className="mb-1 text-sm text-neutral-300">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(num(e.target.value, 0))}
        className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm outline-none focus:border-neutral-500"
      />
    </label>
  );
}

/* ===========================
   Router
   =========================== */
function Redirect({ to }: { to: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);
  return null;
}

function NotFound() {
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <div className="text-3xl font-semibold mb-3">404</div>
        <div className="text-neutral-400 mb-6">Page not found.</div>
        <Link to="/create_launch" className="text-indigo-400 underline">
          Go to Create Launch
        </Link>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<Redirect to="/create_launch" />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/create_launch" element={<CreateLaunchPage />} />
        <Route path="/launches/:id" element={<LaunchDetailPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </HashRouter>
  );
}
